// Usage: calculateShipping(postcode, weight, length, width, height, fuelSurchargeRate, deliveryType)
// Data files: postcodeToZone.json, zonePricing.json

import postcodeToZone from './postcodeToZone.json';
import zonePricing from './zonePricing.json';

// ========== CONSTANTS ==========
const SURCHARGES = {
  standard: 0.80,    // 标准配送
  signature: 1.75,   // 签名配送
  collection: 3.00,  // 上门取件
  oversize: 15.00,   // 超长附加费 (>=1.05m)
};

const CUBIC_CONVERSION_RATE = 250; // kg per cubic metre

// ========== HELPERS ==========

/**
 * 计算体积重量
 * @param {number} lengthCm - 长度(厘米)
 * @param {number} widthCm - 宽度(厘米)
 * @param {number} heightCm - 高度(厘米)
 * @returns {number} 体积重量(kg)
 */
function calculateCubicWeight(lengthCm, widthCm, heightCm) {
  const lengthM = lengthCm / 100;
  const widthM = widthCm / 100;
  const heightM = heightCm / 100;
  return lengthM * widthM * heightM * CUBIC_CONVERSION_RATE;
}

/**
 * 根据邮编查找分区
 * @param {string} postcode - 澳洲邮编
 * @returns {string|null} 分区名称
 */
function getZoneByPostcode(postcode) {
  const pc = String(postcode).trim();
  return postcodeToZone[pc] || null;
}

/**
 * 计算基础运费 (不含燃油和附加费)
 * @param {string} zone - 分区名称
 * @param {number} weight - 计费重量(kg)
 * @returns {number|null} 基础运费(AUD, excl fuel)
 */
function calculateBaseShipping(zone, weight) {
  const pricing = zonePricing[zone];
  if (!pricing) return null;

  if (weight <= 0.3) return pricing['300gm'];
  if (weight <= 0.5) return pricing['500gm'];
  if (weight <= 1)   return pricing['501gm_1kg'];
  if (weight <= 3)   return pricing['1_01kg_3kg'];
  if (weight <= 5)   return pricing['3_01kg_5kg'];
  if (weight <= 25) {
    // 5.01kg-25kg: Base covers 5kg, per_kg starts from 6th kg
    const extraKgs = Math.ceil(weight);
    return pricing['base_5_25kg'] + extraKgs * pricing['per_kg'];
  }

  // Over 25kg - not covered by this pricing
  return null;
}

// ========== CALCULATION ==========

/**
 * 计算完整运费 (含燃油附加费，不含 GST)
 * @param {string} postcode - 邮编
 * @param {number} actualWeight - 实际重量(kg)
 * @param {number} lengthCm - 长度(cm)
 * @param {number} widthCm - 宽度(cm)
 * @param {number} heightCm - 高度(cm)
 * @param {number} fuelSurchargeRate - 燃油附加费率 (e.g. 0.15 for 15%)
 * @param {string} deliveryType - 配送类型: 'standard'|'signature'
 * @returns {Object} 计算结果
 */
function calculateFreight(postcode, actualWeight, lengthCm, widthCm, heightCm, fuelSurchargeRate = 0, deliveryType = 'standard') {
  const zone = getZoneByPostcode(postcode);

  const result = {
    postcode: String(postcode).trim(),
    zone: null,
    cubicWeight: 0,
    billableWeight: 0,
    baseShipping: 0,
    fuelSurchargeRate: 0,
    shippingWithFuel: 0,
    deliverySurcharge: 0,
    oversizeSurcharge: 0,
    totalFreight: 0,
    isOversize: false,
    deliveryType: deliveryType,
    error: null,
  };

  if (!zone) {
    result.error = `No zone found for postcode ${postcode}`;
    return result;
  }
  result.zone = zone;

  const cubicWeight = calculateCubicWeight(lengthCm, widthCm, heightCm);
  const billableWeight = Math.max(actualWeight, cubicWeight);

  // 检查尺寸限制: Excel 只检查 B2 <= 180cm，无单独超长附加费
  const maxDimension = Math.max(lengthCm, widthCm, heightCm);
  const isOversize = false; // Excel 公式中无超长附加费，尺寸限制由外层 IF(B2<=180) 控制

  result.cubicWeight = +cubicWeight.toFixed(4);
  result.billableWeight = +billableWeight.toFixed(4);
  result.isOversize = isOversize;
  result.fuelSurchargeRate = fuelSurchargeRate;

  if (billableWeight > 25) {
    result.error = 'Billable weight exceeds 25kg, out of rate card range';
    return result;
  }

  // Excel 尺寸限制: IF(AND(B2<=180, ...), ..., "")
  if (maxDimension > 180) {
    result.error = `Max dimension ${maxDimension}cm exceeds 180cm limit`;
    return result;
  }

  const baseShipping = calculateBaseShipping(zone, billableWeight);
  if (baseShipping === null) {
    result.error = 'Unable to calculate shipping cost';
    return result;
  }

  result.baseShipping = +baseShipping.toFixed(4);

  // 基础运费 × (1 + 燃油费率)
  const shippingWithFuel = baseShipping * (1 + fuelSurchargeRate);
  result.shippingWithFuel = +shippingWithFuel.toFixed(4);

  // 附加费
  const oversizeSurcharge = isOversize ? SURCHARGES.oversize : 0;
  const standardSurcharge = SURCHARGES.standard; // +0.8 固定附加费 (匹配 Excel 公式)

  result.deliverySurcharge = +standardSurcharge.toFixed(4);
  result.oversizeSurcharge = +oversizeSurcharge.toFixed(4);

  // 总运费: 基础运费×(1+燃油费率) + 0.8 固定附加费 + 超长附加费
  const totalFreight = shippingWithFuel + standardSurcharge + oversizeSurcharge;
  result.totalFreight = +totalFreight.toFixed(4);

  return result;
}

// 适配器函数，匹配其他快递公司的接口: (postcode, weight, length, width, height)
// 默认使用 standard delivery, 燃油费率 0%
export const calculateShipping = (postcode, weight, length, width = 30, height = 30, fuelSurchargeRate = 0, deliveryType = 'standard') => {
  const result = calculateFreight(postcode, weight, length, width, height, fuelSurchargeRate, deliveryType);

  // 如果有错误，返回一个很大的值
  if (result.error) {
    return 999999;
  }

  return result.totalFreight;
};

// 导出详细计算函数和辅助函数供需要的地方使用
export { calculateFreight, getZoneByPostcode, calculateCubicWeight };

/*
使用说明
1. calculateShipping(postcode, weight, length, [width], [height], [fuelSurchargeRate], [deliveryType]) - 简化接口，返回运费总额
2. calculateFreight(postcode, weight, length, width, height, [fuelSurchargeRate], [deliveryType]) - 详细接口，返回完整计算信息
3. 参数说明:
   - postcode: 邮编 (必填)
   - weight: 实际重量 (kg)
   - length: 长度 (cm)
   - width: 宽度 (cm, 默认 30)
   - height: 高度 (cm, 默认 30)
   - fuelSurchargeRate: 燃油附加费率 (0.15 = 15%, 默认 0)
   - deliveryType: 配送类型 ('standard' 或 'signature', 默认 'standard', 保留参数但不影响计算)
4. 体积重量计算: (长 × 宽 × 高) ÷ 1,000,000 × 250 (立方米转 kg)
5. 计费重量: MAX(实际重量, 体积重量)
6. 附加费用:
   - 固定附加费: $0.80 (匹配 Excel 公式 +0.8)
   - 超长附加费 (≥105cm): $15.00
7. 运费公式: 基础运费 × (1 + 燃油费率) + 0.80 + 超长附加费 (不含 GST)
8. 重量范围: 0-25kg (超过 25kg 返回错误)
9. 所有计算结果保留 4 位小数
*/
